import { Controller } from "@hotwired/stimulus";
import { shortcutBlocked, isTypingTarget } from "../lib/shortcut_guard";

export default class extends Controller {
    static targets = ["submitButton", "checkbox", "select", "text"];
    // button: 'true' in feedback mode — the tap submits AND the answer is revealed.
    // autoSubmit: the tap submits (feedback mode, or a learner whose auto-advance
    // preference is 'always' in a no-feedback exam) — it never reveals anything.
    static values = { allowMultiple: Boolean, button: String, autoSubmit: Boolean, explanation: String, explanationLabel: String, willRedirect: Boolean };

    // Add this method to setup the event listener when the controller is connected
    connect() {
        this.boundHandleKeydown = this.handleKeydown.bind(this);
        this.boundApplyInstantFeedback = this.applyInstantFeedback.bind(this);
        this.boundGuardSubmit = this.guardSubmit.bind(this);

        document.addEventListener("keydown", this.boundHandleKeydown);
        this.submitButtonTarget.focus();

        const form = this.element.querySelector('form');
        if (form) {
            // Bubble-phase guard: block an empty submit no matter how it was triggered (button,
            // swipe-nav, Enter, iOS bfcache restoring an enabled button over a reset checkbox,
            // programmatic requestSubmit). Turbo decides whether to submit in its own document-level
            // bubble handler by checking event.defaultPrevented; a form-level bubble listener runs
            // first, so preventDefault() here stops Turbo too. The server rejects a blank answer and
            // the disabled button only covers some paths — this closes the rest. See
            // SKILLOUT-PRODUCTION-7A (crash, fixed server-side) and -M4 (residual iOS empty submits).
            form.addEventListener('submit', this.boundGuardSubmit);
            // Listen for form submission to apply instant feedback
            form.addEventListener('turbo:submit-start', this.boundApplyInstantFeedback);
        }
    }

    // Add this method to remove the event listener when the controller is disconnected
    disconnect() {
        document.removeEventListener("keydown", this.boundHandleKeydown);

        const form = this.element.querySelector('form');
        if (form) {
            form.removeEventListener('submit', this.boundGuardSubmit);
            form.removeEventListener('turbo:submit-start', this.boundApplyInstantFeedback);
        }
    }

    // Stop the form from submitting with nothing the server will receive as an answer, whatever the
    // question type. Note `checked && !disabled`: a disabled checkbox is NOT serialized into the POST even
    // though its `.checked` stays true, so it must not count as a selection. Feedback mode disables the
    // inputs the instant an answer is submitted (applyInstantFeedback); without this, a second
    // submit fired during the network-latency window (double-tap/swipe/Enter before the next question
    // loads) sends an empty `answer` and is logged as SKILLOUT-PRODUCTION-7A/-M4.
    guardSubmit(event) {
        if (!this.hasSubmittableAnswer) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    // True when the browser will actually include a usable `answer` in the submitted form data:
    // a checked, enabled checkbox; every matching tile's select filled; or a non-blank free-text answer.
    get hasSubmittableAnswer() {
        const checkboxes = this.checkboxTargets;
        if (checkboxes.length) {
            return checkboxes.some(checkbox => checkbox.checked && !checkbox.disabled);
        }
        const selects = this.selectTargets;
        if (selects.length) {
            return selects.every(select => select.value !== "" && !select.disabled);
        }
        const text = this.textTargets[0];
        return Boolean(text && !text.disabled && text.value.trim() !== "");
    }

    // Digits 1–9 toggle the option in that position. Enter, the arrows and ? belong to
    // question_keys_controller (page level, it also drives the review screen); a digit
    // only selects there too — Enter confirms, unlike a click in feedback mode.
    handleKeydown(event) {
        // Digits typed into a free-text answer or a tile select are content, not hotkeys.
        if (shortcutBlocked(event) || isTypingTarget(event.target)) return;
        if (!/^[1-9]$/.test(event.key)) return;

        const checkbox = this.checkboxTargets[parseInt(event.key, 10) - 1];

        // Only toggle checkboxes that are not disabled
        if (checkbox && !checkbox.disabled) {
            checkbox.checked = !checkbox.checked;
            this.toggleSubmit({target: checkbox});
            // Tags the Question Answered event (Courses::TestResultItemsController).
            const form = this.element.querySelector('form');
            if (form && form.elements.input_method) form.elements.input_method.value = 'keyboard';
        }
    }

    submitButtonTargetConnected(element) {
        this.toggleSubmit(element);
    }

    toggleSubmit(event) {
        if (this.hasSubmittableAnswer) {
            this.submitButtonTarget.removeAttribute("disabled");
            // Keep focus in a free-text box while the learner is still typing.
            if (!(event.target && event.target.tagName === 'TEXTAREA')) {
                this.submitButtonTarget.focus();
            }
        } else {
            this.submitButtonTarget.setAttribute("disabled", "disabled");
        }

        if (this.allowMultipleValue) {
            return;
        }

        if (typeof event.target === 'undefined' || event.target.type !== 'checkbox') {
            return;
        }

        if (event.target.checked) {
            this.checkboxTargets.forEach((checkbox) => {
                if (checkbox !== event.target) {
                    checkbox.checked = false;
                }
            });
        }
    }

    submitFormOnClick(event) {
        if (this.allowMultipleValue) {
            return;
        }
        if (this.buttonValue == 'true' || this.autoSubmitValue) {
            this.toggleSubmit(event);
            // Bail if nothing submittable is selected (deselected the only option, or the boxes were
            // already disabled by a prior answer) — a blank answer is rejected (SKILLOUT-PRODUCTION-7A).
            if (!this.hasSubmittableAnswer) return;
            this.submitButtonTarget.click();
        }
    }

    applyInstantFeedback(event) {
        // Skip everything if this submission will trigger daily limit redirect
        if (this.willRedirectValue === true) {
            return;
        }

        // Lock the form in every mode: the dimmed Next button is the only
        // feedback an exam-mode learner gets while the POST is in flight, and
        // it prevents a double submit.
        this.lockForm();

        // The answer reveal itself only applies when show_correct_answer is enabled
        if (this.buttonValue !== 'true') {
            return;
        }

        // Free text has no client-side key: the server's answered form gives the feedback.
        if (!this.checkboxTargets.length && !this.selectTargets.length) {
            return;
        }

        if (this.selectTargets.length) {
            this.applyMatchingFeedback();
            return;
        }

        // Get all selected answer values
        const selectedValues = this.checkboxTargets
            .filter(checkbox => checkbox.checked)
            .map(checkbox => checkbox.value);

        // Get all labels with answer data
        const labels = this.element.querySelectorAll('label[data-answer-value]');

        // Determine if the answer is correct (all correct answers selected, no incorrect ones)
        let isAnswerCorrect = false;
        const correctAnswers = [];

        labels.forEach(label => {
            if (label.dataset.answerCorrect === 'true') {
                correctAnswers.push(label.dataset.answerValue);
            }
        });

        // Check if selected answers match correct answers exactly
        const sortedSelected = [...selectedValues].sort();
        const sortedCorrect = [...correctAnswers].sort();
        isAnswerCorrect = sortedSelected.length === sortedCorrect.length &&
            sortedSelected.every((val, index) => val === sortedCorrect[index]);

        labels.forEach(label => {
            const answerValue = label.dataset.answerValue;
            const isCorrect = label.dataset.answerCorrect === 'true';
            const isSelected = selectedValues.includes(answerValue);

            // Reset to base answer-option, then layer the revealed state.
            label.classList.remove(
                'answer-option--default',
                'answer-option--selected',
                'answer-option--correct',
                'answer-option--incorrect'
            );

            if (isCorrect) {
                label.classList.add('answer-option--correct');
            } else if (isSelected) {
                label.classList.add('answer-option--incorrect');
            } else {
                label.classList.add('answer-option--default');
            }
        });

        // Show explanation if it exists
        this.showExplanation();

        // Update progress navigation dot
        this.updateProgressDot(isAnswerCorrect);

        // Haptic feedback on wrong answer — native Android app only
        if (!isAnswerCorrect &&
            navigator.userAgent.includes("Hotwire Native") &&
            typeof navigator.vibrate === 'function') {
            navigator.vibrate([80, 40, 80]);
        }
    }

    // Matching question: every tile must carry the number of its correct name.
    applyMatchingFeedback() {
        const tiles = Array.from(this.element.querySelectorAll('[data-matching-tile]'));
        let isAnswerCorrect = tiles.length > 0;

        tiles.forEach(tile => {
            const select = tile.querySelector('select');
            const tileCorrect = Boolean(select) && select.value === tile.dataset.answerCorrect;
            if (!tileCorrect) isAnswerCorrect = false;
            tile.classList.remove('answer-tile--default', 'answer-tile--correct', 'answer-tile--incorrect');
            tile.classList.add(tileCorrect ? 'answer-tile--correct' : 'answer-tile--incorrect');
            if (select) select.disabled = true;
        });

        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true;
        }

        this.showExplanation();
        this.updateProgressDot(isAnswerCorrect);

        if (!isAnswerCorrect &&
            navigator.userAgent.includes("Hotwire Native") &&
            typeof navigator.vibrate === 'function') {
            navigator.vibrate([80, 40, 80]);
        }
    }

    updateProgressDot(isCorrect) {
        // Flip the current cell in the navigator grid. The dropdown is closed while
        // answering, so this is a best-effort cosmetic update — the server re-renders
        // the navigator on advance. Safe no-op when the cell is absent.
        const currentCell = document.querySelector('.qnav-cell-current');
        if (currentCell) {
            currentCell.classList.remove('qnav-cell-current');
            currentCell.classList.add(isCorrect ? 'qnav-cell-correct' : 'qnav-cell-incorrect');
        }
    }

    showExplanation() {
        // Check if explanation exists and has content
        if (!this.explanationValue || this.explanationValue.trim() === '') {
            return;
        }

        // Find the form element to insert explanation after it
        const form = this.element.querySelector('form');
        if (!form) {
            return;
        }

        // Check if explanation already exists (prevent duplicates)
        const existingExplanation = this.element.parentNode.querySelector('.explanation-container');
        if (existingExplanation) {
            return;
        }

        // Create explanation div matching answered form structure
        const explanationDiv = document.createElement('div');
        explanationDiv.className = 'explanation-container mt-4 p-4 sm:px-5 rounded-xl bg-primary-100 dark:bg-primary-800/60';

        const eyebrow = document.createElement('div');
        eyebrow.className = 'inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-primary-600 dark:text-primary-300 mb-1.5';
        const eyebrowIcon = document.createElement('i');
        eyebrowIcon.className = 'bi bi-info-circle text-[12px]';
        eyebrowIcon.setAttribute('aria-hidden', 'true');
        eyebrow.appendChild(eyebrowIcon);
        eyebrow.appendChild(document.createTextNode(' ' + (this.explanationLabelValue || 'Explanation')));

        const explanationText = document.createElement('p');
        explanationText.className = 'm-0 text-[14px] leading-[1.6] text-primary-800 dark:text-primary-100';
        explanationText.textContent = this.explanationValue;

        explanationDiv.appendChild(eyebrow);
        explanationDiv.appendChild(explanationText);

        // Insert right after this controller's element (the space-y-5 question
        // block), not inside it: inside, space-y would give the form a bottom
        // margin the server render doesn't have, and the panel would hop 4px
        // when the real response lands. Fully visible: the learner is waiting
        // to read it, and this runs on every answered question, so no fade.
        this.element.parentNode.insertBefore(explanationDiv, this.element.nextSibling);
    }

    lockForm() {
        this.checkboxTargets.forEach(checkbox => {
            checkbox.disabled = true;
        });

        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true;
            this.submitButtonTarget.setAttribute('aria-busy', 'true');
        }
    }

    get checkboxTargets() {
        return Array.from(this.element.querySelectorAll("input[type=checkbox]"));
    }

    get selectTargets() {
        return Array.from(this.element.querySelectorAll("select[data-checkbox-target=select]"));
    }

    get textTargets() {
        return Array.from(this.element.querySelectorAll("textarea[data-checkbox-target=text]"));
    }
}
